import React, { useState } from "react";
import { T } from "./theme";
import { SectionHeader, Card, Btn, Badge, Input, Sel, Modal, Note, Progress, Textarea } from "./ui";
import { Icon } from "./Icons";

// ═══════════════════════════════════════════════════════════════════════
// LOGÍSTICA + SUPORTE — módulo integrado para time operacional
// ═══════════════════════════════════════════════════════════════════════

const ETAPAS = [
  "Aguardando coleta",
  "Em transporte",
  "Em rota de entrega",
  "Entrega agendada",
  "Entregue",
];

export default function Logistica({ pedidos, setPedidos, tickets, setTickets }) {
  const [aba, setAba] = useState("logistica");
  const [editandoPedido, setEditandoPedido] = useState(null);
  const [editandoTicket, setEditandoTicket] = useState(null);

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 24 }}>
      <SectionHeader
        title="Operações"
        subtitle="Logística e Suporte integrados — mesmo painel"
        icon={<Icon.truck size={24} />}
      />

      {/* Abas */}
      <div style={{ display: "flex", gap: 6 }}>
        <AbaButton ativa={aba === "logistica"} onClick={() => setAba("logistica")} icon={<Icon.truck size={14} />}>
          Logística ({pedidos.length})
        </AbaButton>
        <AbaButton ativa={aba === "suporte"} onClick={() => setAba("suporte")} icon={<Icon.support size={14} />}>
          Suporte ({tickets.filter(t => t.status !== "resolvido").length} abertos)
        </AbaButton>
      </div>

      {aba === "logistica" && (
        <LogisticaPanel
          pedidos={pedidos} setPedidos={setPedidos}
          editando={editandoPedido} setEditando={setEditandoPedido}
        />
      )}

      {aba === "suporte" && (
        <SuportePanel
          tickets={tickets} setTickets={setTickets}
          editando={editandoTicket} setEditando={setEditandoTicket}
        />
      )}
    </div>
  );
}

function AbaButton({ ativa, onClick, icon, children }) {
  return (
    <button onClick={onClick} style={{
      background: ativa ? T.brand : "transparent",
      color: ativa ? T.textInv : T.textSub,
      border: `1px solid ${ativa ? T.brand : T.borderMid}`,
      borderRadius: T.r.md, padding: "10px 18px",
      fontSize: 12, fontWeight: 500, cursor: "pointer",
      display: "flex", alignItems: "center", gap: 7,
      transition: `all 160ms ${T.ease}`,
    }}>
      {icon} {children}
    </button>
  );
}

// ═══════════════════════════════════════════════════════════════════════
// LOGÍSTICA
// ═══════════════════════════════════════════════════════════════════════

function LogisticaPanel({ pedidos, setPedidos, editando, setEditando }) {
  function salvar(p) {
    setPedidos(pedidos.map(x => x.id === p.id ? p : x));
    setEditando(null);
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      <Note tone="brand" icon={<Icon.info size={14} />}>
        Acompanhe e atualize status dos pedidos. Adicione fotos de coleta e entrega para documentar o processo.
      </Note>

      {pedidos.map(p => (
        <Card key={p.id} style={{ padding: 18 }}>
          <div style={{ display: "flex", justifyContent: "space-between", gap: 14, flexWrap: "wrap", marginBottom: 14 }}>
            <div style={{ flex: 1, minWidth: 220 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4, flexWrap: "wrap" }}>
                <div className="serif" style={{ fontSize: 18, fontWeight: 600, color: T.brand, letterSpacing: -0.2 }}>
                  Pedido {p.id}
                </div>
                {p.urgente && <Badge text="URGENTE" tone="err" />}
                <Badge text={ETAPAS[p.etapa]} tone={p.etapa === 4 ? "ok" : p.etapa >= 2 ? "warn" : "info"} />
              </div>
              <div style={{ fontSize: 13, color: T.text, fontWeight: 500 }}>{p.cliente}</div>
              <div style={{ fontSize: 11, color: T.textSub, marginTop: 4 }}>{p.itens}</div>
              <div style={{ fontSize: 11, color: T.textSub, marginTop: 6, display: "flex", gap: 12, flexWrap: "wrap" }}>
                <span><Icon.pin size={10} /> {p.cidade}</span>
                <span>Previsão: <b>{p.previsao}</b></span>
                {p.transportadora && <span>Via: <b>{p.transportadora}</b></span>}
              </div>
            </div>
            <div>
              <Btn variant="ghost" icon={<Icon.edit size={12} />} onClick={() => setEditando(p)}>Gerenciar</Btn>
            </div>
          </div>

          {/* Timeline de status */}
          <div style={{ display: "flex", gap: 4, alignItems: "center" }}>
            {ETAPAS.map((etapa, i) => (
              <React.Fragment key={i}>
                <div title={etapa} style={{
                  width: 18, height: 18, borderRadius: "50%",
                  background: i <= p.etapa ? T.ok : T.elev,
                  border: `2px solid ${i <= p.etapa ? T.ok : T.borderMid}`,
                  flexShrink: 0,
                  display: "flex", alignItems: "center", justifyContent: "center",
                  color: T.textInv, fontSize: 10, fontWeight: 700,
                }}>{i <= p.etapa ? "✓" : ""}</div>
                {i < ETAPAS.length - 1 && (
                  <div style={{ flex: 1, height: 2, background: i < p.etapa ? T.ok : T.elev }} />
                )}
              </React.Fragment>
            ))}
          </div>
          <div style={{ display: "flex", justifyContent: "space-between", marginTop: 6, fontSize: 9, color: T.textDim }}>
            {ETAPAS.map((e, i) => (
              <div key={i} style={{ flex: 1, textAlign: i === 0 ? "left" : i === ETAPAS.length - 1 ? "right" : "center" }}>
                {e}
              </div>
            ))}
          </div>

          {/* Fotos */}
          {(p.fotoColeta || p.fotoEntrega) && (
            <div style={{ display: "flex", gap: 10, marginTop: 14 }}>
              {p.fotoColeta && (
                <div style={{
                  flex: 1, background: T.cardSoft, borderRadius: T.r.md,
                  padding: 10, border: `1px solid ${T.border}`,
                  display: "flex", alignItems: "center", gap: 8,
                }}>
                  <Icon.camera size={14} />
                  <div style={{ fontSize: 11 }}>
                    <div style={{ color: T.textSub, fontSize: 9, textTransform: "uppercase", letterSpacing: 0.5, fontWeight: 600 }}>Foto coleta</div>
                    <div style={{ color: T.ok, fontWeight: 600 }}>✓ anexada</div>
                  </div>
                </div>
              )}
              {p.fotoEntrega && (
                <div style={{
                  flex: 1, background: T.cardSoft, borderRadius: T.r.md,
                  padding: 10, border: `1px solid ${T.border}`,
                  display: "flex", alignItems: "center", gap: 8,
                }}>
                  <Icon.camera size={14} />
                  <div style={{ fontSize: 11 }}>
                    <div style={{ color: T.textSub, fontSize: 9, textTransform: "uppercase", letterSpacing: 0.5, fontWeight: 600 }}>Foto entrega</div>
                    <div style={{ color: T.ok, fontWeight: 600 }}>✓ anexada</div>
                  </div>
                </div>
              )}
            </div>
          )}
        </Card>
      ))}

      {editando && <PedidoEditor pedido={editando} onSave={salvar} onCancel={() => setEditando(null)} />}
    </div>
  );
}

function PedidoEditor({ pedido, onSave, onCancel }) {
  const [p, setP] = useState(pedido);

  return (
    <Modal title={`Pedido ${p.id}`} onClose={onCancel} width={480}>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
        <Sel label="Status atual" value={p.etapa} onChange={e => setP({ ...p, etapa: Number(e.target.value) })}>
          {ETAPAS.map((e, i) => <option key={i} value={i}>{i + 1}. {e}</option>)}
        </Sel>
        <Input label="Previsão" value={p.previsao} onChange={e => setP({ ...p, previsao: e.target.value })} />
      </div>
      <Input label="Transportadora" value={p.transportadora} onChange={e => setP({ ...p, transportadora: e.target.value })} />

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginTop: 8 }}>
        <button onClick={() => setP({ ...p, fotoColeta: p.fotoColeta ? null : "anexada" })} style={{
          background: p.fotoColeta ? T.okSoft : T.cardSoft,
          border: `1px dashed ${p.fotoColeta ? T.ok : T.borderMid}`,
          borderRadius: T.r.md, padding: 16, cursor: "pointer",
          display: "flex", flexDirection: "column", alignItems: "center", gap: 6,
          color: p.fotoColeta ? T.ok : T.textSub,
        }}>
          <Icon.camera size={20} />
          <span style={{ fontSize: 11, fontWeight: 600 }}>
            {p.fotoColeta ? "✓ Foto coleta (remover)" : "Adicionar foto coleta"}
          </span>
        </button>
        <button onClick={() => setP({ ...p, fotoEntrega: p.fotoEntrega ? null : "anexada" })} style={{
          background: p.fotoEntrega ? T.okSoft : T.cardSoft,
          border: `1px dashed ${p.fotoEntrega ? T.ok : T.borderMid}`,
          borderRadius: T.r.md, padding: 16, cursor: "pointer",
          display: "flex", flexDirection: "column", alignItems: "center", gap: 6,
          color: p.fotoEntrega ? T.ok : T.textSub,
        }}>
          <Icon.camera size={20} />
          <span style={{ fontSize: 11, fontWeight: 600 }}>
            {p.fotoEntrega ? "✓ Foto entrega (remover)" : "Adicionar foto entrega"}
          </span>
        </button>
      </div>

      <Note tone="info">
        <b>Demo:</b> Simulação de upload. Em produção, fotos serão enviadas via câmera do smartphone do entregador e armazenadas com timestamp e geolocalização.
      </Note>

      <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
        <Btn variant="ghost" onClick={onCancel}>Cancelar</Btn>
        <Btn onClick={() => onSave(p)} icon={<Icon.check size={14} />}>Salvar</Btn>
      </div>
    </Modal>
  );
}

// ═══════════════════════════════════════════════════════════════════════
// SUPORTE
// ═══════════════════════════════════════════════════════════════════════

function SuportePanel({ tickets, setTickets, editando, setEditando }) {
  const [filtro, setFiltro] = useState("todos");

  const filtrados = tickets.filter(t => {
    if (filtro === "abertos") return t.status !== "resolvido";
    if (filtro === "resolvidos") return t.status === "resolvido";
    return true;
  });

  function salvar(t) {
    setTickets(tickets.map(x => x.id === t.id ? t : x));
    setEditando(null);
  }

  const abertos = tickets.filter(t => t.status !== "resolvido").length;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      <div style={{ display: "flex", gap: 6 }}>
        {[
          { id: "todos", label: `Todos (${tickets.length})` },
          { id: "abertos", label: `Abertos (${abertos})` },
          { id: "resolvidos", label: `Resolvidos (${tickets.length - abertos})` },
        ].map(f => (
          <button key={f.id} onClick={() => setFiltro(f.id)} style={{
            background: filtro === f.id ? T.brand : "transparent",
            color: filtro === f.id ? T.textInv : T.textSub,
            border: `1px solid ${filtro === f.id ? T.brand : T.borderMid}`,
            borderRadius: T.r.md, padding: "6px 12px",
            fontSize: 11, fontWeight: 500, cursor: "pointer",
          }}>{f.label}</button>
        ))}
      </div>

      {filtrados.map(t => (
        <Card key={t.id} style={{ padding: 16 }}>
          <div style={{ display: "flex", justifyContent: "space-between", gap: 10, flexWrap: "wrap" }}>
            <div style={{ flex: 1, minWidth: 220 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4, flexWrap: "wrap" }}>
                <Badge text={t.prioridade} tone={t.prioridade === "alta" ? "err" : t.prioridade === "media" ? "warn" : "ok"} />
                <Badge text={t.status} tone={t.status === "resolvido" ? "ok" : t.status === "em andamento" ? "warn" : "info"} />
                <span style={{ fontSize: 11, color: T.textDim }}>{t.tempo}</span>
              </div>
              <div style={{ fontSize: 14, fontWeight: 500, color: T.text, marginBottom: 4 }}>{t.assunto}</div>
              <div style={{ fontSize: 11, color: T.textSub, display: "flex", gap: 10, flexWrap: "wrap" }}>
                {t.tipo === "cliente" && <span>Cliente: <b>{t.cliente}</b></span>}
                <span>Corretor: <b>{t.corretor}</b></span>
                <span>Categoria: <b>{t.categoria}</b></span>
              </div>
            </div>
            <Btn variant="ghost" icon={<Icon.edit size={12} />} onClick={() => setEditando(t)}>Gerenciar</Btn>
          </div>
        </Card>
      ))}

      {filtrados.length === 0 && (
        <Card>
          <div style={{ textAlign: "center", padding: 40, color: T.textDim, fontSize: 13 }}>
            Nenhum ticket com esse filtro.
          </div>
        </Card>
      )}

      {editando && <TicketEditor ticket={editando} onSave={salvar} onCancel={() => setEditando(null)} />}
    </div>
  );
}

function TicketEditor({ ticket, onSave, onCancel }) {
  const [t, setT] = useState(ticket);

  return (
    <Modal title={`Ticket #${t.id}`} onClose={onCancel} width={520}>
      <Note tone="brand">{t.assunto}</Note>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
        <Sel label="Prioridade" value={t.prioridade} onChange={e => setT({ ...t, prioridade: e.target.value })}>
          <option value="alta">Alta</option>
          <option value="media">Média</option>
          <option value="baixa">Baixa</option>
        </Sel>
        <Sel label="Status" value={t.status} onChange={e => setT({ ...t, status: e.target.value })}>
          <option value="aberto">Aberto</option>
          <option value="em andamento">Em andamento</option>
          <option value="resolvido">Resolvido</option>
        </Sel>
      </div>

      <Textarea label="Notas internas" value={t.notas || ""} onChange={e => setT({ ...t, notas: e.target.value })} rows={4} placeholder="Adicione notas sobre o andamento..." />

      <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
        <Btn variant="ghost" onClick={onCancel}>Cancelar</Btn>
        <Btn onClick={() => onSave(t)} icon={<Icon.check size={14} />}>Salvar</Btn>
      </div>
    </Modal>
  );
}
